import { useState, useMemo } from 'react'
import TrackerCaseDrawer from './TrackerDetailDrawer'
import { statusClasses, fmtDate, prettyStatus, awaitingLabel, LANES, LANE_BY_ID } from './trackerFields'
import { Spinner } from './trackerShared'

// The LIST view of the standup board — the dense, one-case-per-row fallback to
// the lane board (TrackerBoard.jsx), and what renders below `lg` where lanes do
// not fit. Risk-driven rows: a coloured left bar + a DUE pill.
// v6: the stage chip is the case's col-F Status, verbatim (was col CU).
// NO PATIENT DATA — a case is identified by its Case ID.

// risk tier -> left-bar + accent classes
const RISK = {
  critical: { bar: 'border-l-red-500',   dot: 'bg-red-500' },
  at_risk:  { bar: 'border-l-amber-500', dot: 'bg-amber-500' },
  no_date:  { bar: 'border-l-amber-300', dot: 'bg-amber-300' },
  on_track: { bar: 'border-l-emerald-400', dot: 'bg-emerald-400' },
}
const riskBar = (r) => (RISK[r] || RISK.on_track).bar

// The prominent DUE pill: label + sub + tone. Urgency (red/amber) only applies
// while there's work left; a case with everything done reads as "Ready" (green),
// so the pill never fights the green risk-bar.
function dueMeta(c) {
  const d = c.daysToDispatch
  const green = 'bg-emerald-50 text-emerald-700 border-emerald-200'
  if (c.pending === 0) {
    return { label: 'Ready', sub: c.dispatchPlan ? `due ${fmtDate(c.dispatchPlan)}` : 'all STLs done', tone: green }
  }
  if (c.dispatchPlan == null || d == null) {
    return { label: 'No due date', sub: 'set in sheet', tone: 'bg-amber-50 text-amber-700 border-amber-200' }
  }
  const date = fmtDate(c.dispatchPlan)
  if (d < 0) return { label: 'OVERDUE', sub: `${date} · ${-d}d late`, tone: 'bg-red-100 text-red-700 border-red-300' }
  if (d === 0) return { label: 'DUE TODAY', sub: date, tone: 'bg-red-100 text-red-700 border-red-300' }
  if (d <= 3) return { label: `DUE in ${d}d`, sub: date, tone: 'bg-amber-100 text-amber-800 border-amber-300' }
  return { label: `Due ${date}`, sub: `in ${d}d`, tone: green }
}

// per-state histogram -> "5 queued · 1 printing"
function stateChips(states = {}) {
  return Object.entries(states).sort((a, b) => b[1] - a[1]).map(([k, n]) => `${n} ${k.toLowerCase()}`)
}

const SORTS = {
  stage:    { label: 'Pipeline stage', cmp: (a, b) => rankLane(a.lane) - rankLane(b.lane) || (b.ageDays || 0) - (a.ageDays || 0) || cmpNo(a, b) },
  priority: { label: 'Priority (risk)', cmp: (a, b) => rankRisk(a.risk) - rankRisk(b.risk) || nn(a.daysToDispatch) - nn(b.daysToDispatch) || (b.ageDays || 0) - (a.ageDays || 0) || cmpNo(a, b) },
  dispatch: { label: 'Dispatch date',   cmp: (a, b) => nn(a.daysToDispatch) - nn(b.daysToDispatch) || cmpNo(a, b) },
  age:      { label: 'Age (oldest)',    cmp: (a, b) => (b.ageDays || 0) - (a.ageDays || 0) || cmpNo(a, b) },
}
const rankRisk = (r) => ({ critical: 0, at_risk: 1, no_date: 2, on_track: 3 }[r] ?? 3)
const rankLane = (id) => { const i = LANES.findIndex((l) => l.id === id); return i < 0 ? LANES.length : i }
const nn = (v) => (v == null ? Infinity : v) // nulls (no date) sort last
const cmpNo = (a, b) => (b.caseNo || '').localeCompare(a.caseNo || '')

export default function TrackerStandupBoard({ board = [], loading, error }) {
  const [selected, setSelected] = useState(null)
  const [expanded, setExpanded] = useState(() => new Set())
  const [q, setQ] = useState('')
  const [sort, setSort] = useState('stage')

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase()
    // ...c.unknownStates: `stage` is now the RECOGNISED label, so without this
    // the offending sheet word is unsearchable — and with the Unmapped lane no
    // longer collecting these cases, search is the only way to reach them from
    // the "Unknown status N" stat.
    const filtered = !s ? board : board.filter((c) =>
      [c.caseNo, c.doctorName, c.hospitalName, c.owner, c.stage, ...(c.unknownStates || [])]
        .some((v) => (v || '').toLowerCase().includes(s)))
    return [...filtered].sort(SORTS[sort].cmp)
  }, [board, q, sort])

  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n })

  if (loading) return <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center"><Spinner className="h-4 w-4" /> Loading active cases…</div>
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search case, doctor, hospital, owner, status…"
          className="input flex-1 min-w-[240px] text-sm" />
        <label className="text-xs text-gray-500 flex items-center gap-1.5">Sort
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="input text-sm w-auto py-1.5">
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="text-center text-gray-500 text-sm py-12 border border-dashed border-gray-200 rounded-xl">
          {board.length === 0
            ? 'Nothing open. A case shows here while any of its STLs has a non-terminal Status in col F of the sheet — type one, then Sync.'
            : 'No open cases match your search.'}
        </div>
      ) : (
        <ul className="space-y-2.5">
          {rows.map((c) => {
            const isOpen = expanded.has(c.id)
            const due = dueMeta(c)
            const chips = stateChips(c.states)
            return (
              <li key={c.id} className={`border border-gray-200 border-l-4 ${riskBar(c.risk)} rounded-xl bg-white overflow-hidden`}>
                <div className="flex items-start gap-3 p-3.5 hover:bg-gray-50/60 cursor-pointer" onClick={() => toggle(c.id)}>
                  <button aria-label={isOpen ? 'Collapse' : 'Expand'} className="text-gray-400 text-xs mt-1.5 w-3 shrink-0 select-none">{isOpen ? '▾' : '▸'}</button>

                  <div className="min-w-0 flex-1">
                    {/* header — the Case ID is the case's identity here */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="font-mono font-semibold text-gray-900 text-sm">{c.caseNo}</span>
                      {c.lane && <span className={`text-[11px] ${LANE_BY_ID[c.lane]?.head || 'text-gray-500'}`}>{LANE_BY_ID[c.lane]?.label}</span>}
                      {c.stage && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusClasses(c.stage)}`} title={c.stageMixed ? Object.entries(c.openStates || {}).map(([k, n]) => `${n}× ${k}`).join(', ') : c.stage}>
                          {prettyStatus(c.stage)}{c.stageMixed ? ' +' : ''}
                        </span>
                      )}
                      {/* Drift marker. This view is not an alternative layout —
                          it is what EVERY viewport below lg renders, so leaving
                          it out would hide unrecognised statuses from most of
                          the team. Before the lane change these cases at least
                          read "Unmapped"; now this pill is the only trace. */}
                      {c.unknownStates?.length > 0 && (
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium bg-rose-100 text-rose-700"
                          title={`Status the board does not recognise: ${c.unknownStates.join(', ')} — add it to a lane, or fix the sheet`}
                        >? {c.unknownStates.join(', ')}</span>
                      )}
                      {c.awaitingData && awaitingLabel(c.openStates) !== prettyStatus(c.stage) && (
                        // Only when it SAYS something the stage pill does not. Once the two
                        // were made to agree (review round 2) an awaiting-only case rendered
                        // "Awaiting Payment ⚠ Awaiting Payment" in one hue — the whole lane,
                        // 15 cases, duplicated. It earns its place on a mixed On Hold +
                        // Awaiting case, where the pill reads "On Hold" and this says what
                        // is actually being waited for.
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-700">⚠ {awaitingLabel(c.openStates)}</span>
                      )}
                    </div>
                    {/* who / where */}
                    <div className="text-sm text-gray-600 mt-1 flex flex-wrap gap-x-2 items-center">
                      <span>🏥 {c.hospitalName || <span className="text-gray-400 italic">no hospital</span>}</span>
                      {c.doctorName && <span className="text-gray-400">· 🩺 {c.doctorName}</span>}
                    </div>
                    {/* owner / age / complexity / progress */}
                    <div className="text-xs text-gray-500 mt-1.5 flex flex-wrap gap-x-3 gap-y-1 items-center">
                      <span>👤 {c.owner || <span className="italic text-gray-400">no owner</span>}</span>
                      {typeof c.ageDays === 'number' && (
                        c.ageSuspect
                          ? <span className="text-gray-400" title={`DICOM date reads ${c.ageDays}d ago — check the date cell in the sheet`}>⚑ check DICOM date</span>
                          : <span>{c.ageDays}d in pipeline</span>
                      )}
                      {c.complexity && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{c.complexity}</span>}
                      <span className="text-gray-400">{c.done}/{c.partCount} STLs done</span>
                      {chips.map((t) => <span key={t} className="px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-600">{t}</span>)}
                    </div>
                    {c.nextAction && <div className="text-sm mt-1.5"><span className="text-gray-400">Next: </span><span className="text-gray-800">{c.nextAction}</span></div>}
                  </div>

                  {/* prominent DUE pill */}
                  <div className={`shrink-0 text-center rounded-lg border px-3 py-1.5 min-w-[92px] ${due.tone}`}>
                    <div className="text-xs font-bold leading-tight">{due.label}</div>
                    <div className="text-[11px] opacity-80 mt-0.5">{due.sub}</div>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-3.5 py-2 ml-3">
                    <div className="flex justify-end mb-1.5">
                      <button onClick={(e) => { e.stopPropagation(); setSelected(c.id) }} className="text-xs text-brand-600 hover:text-brand-700 hover:underline">Open full details →</button>
                    </div>
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {c.parts.map((p) => (
                          <tr key={p.seq} className="text-gray-700">
                            <td className="py-1.5 pr-3 font-mono text-xs text-gray-600 truncate max-w-[420px]" title={p.stlName}>{p.stlName}{p.partLabel ? ` (${p.partLabel})` : ''}</td>
                            <td className="py-1.5 pr-3 text-gray-500 truncate max-w-[160px]">{p.anatomy || ''}</td>
                            <td className="py-1.5 text-right whitespace-nowrap">
                              {p.status
                                ? <span className={`text-xs px-2 py-0.5 rounded-full ${statusClasses(p.status)}`}>{p.status}</span>
                                : <span className="text-xs text-gray-400">— not started</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {selected && <TrackerCaseDrawer caseId={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}
